import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../hooks/useAuthContext';
import { formatDistanceToNow } from 'date-fns';
import { 
  EyeIcon, 
  ChatBubbleLeftRightIcon,
  UserIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import LikeButton from './LikeButton';
import ModerationControls from './ModerationControls';
import DeletionControls from './DeletionControls';
import { deleteQuestion, updateQuestion } from '../../../services/firebase/forum';
import ReplyModal from './ReplyModal';
import OriginalPostPreview from './OriginalPostPreview';
import ReplyComposer from './ReplyComposer';
import useReplyModal from '../../../hooks/useReplyModal';
import Button from '../../common/Button';
import MobilePostActions from './MobilePostActions';

import UserMentionInput from '../../common/UserMentionInput';
import ContentWithMentions from '../../common/ContentWithMentions';
import AttachmentDisplay from './AttachmentDisplay';
import { filterPresetTags } from '../../../utils/tagUtils';
import useCurrentProfilePicture from '../../../hooks/useCurrentProfilePicture';


const QuestionDetail = ({ question, onLikeUpdate, onEdit, onDelete, onReplySubmitted }) => {
  const { user } = useAuthContext();
  const { isOpen, originalPost, openModal, closeModal } = useReplyModal();
  
  // Use dynamic profile picture that updates when user changes their photo
  const { currentProfilePicture } = useCurrentProfilePicture(question.authorId, question.authorPhotoURL);
  
  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const editorRef = useRef(null);
  const editContainerRef = useRef(null);
  const contentRef = useRef(null);

  // Handle link clicks with event delegation
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleLinkClick = (e) => {
      // Check if the clicked element is a link with our data attribute
      const link = e.target.closest('a[data-external-link="true"]');
      if (link) {
        e.preventDefault();
        e.stopPropagation();
        const href = link.getAttribute('href');
        if (href) {
          window.open(href, '_blank', 'noopener,noreferrer');
        }
      }
    };

    contentElement.addEventListener('click', handleLinkClick);
    
    return () => {
      contentElement.removeEventListener('click', handleLinkClick);
    };
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Handle click outside to save edits
  useEffect(() => {
    const handleClickOutside = async (event) => {
      if (isEditing && editContainerRef.current && !editContainerRef.current.contains(event.target)) {
        await handleSaveEdits();
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isEditing, editedContent]);

  const handleEditContent = () => {
    setIsEditing(true);
    setEditedContent(question.content || '');
  };

  // Edit functionality is now only available through the edit button

  const handleSaveEdits = async () => {
    if (!user?.uid || !question?.id || isSaving) return;

    // Don't save if content hasn't changed
    if (editedContent.trim() === (question.content || '').trim()) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);

    try {
      const result = await updateQuestion(question.id, { content: editedContent.trim() }, user.uid);
      
      if (result.success) {
        // Update the local question data if onEdit callback is provided
        if (onEdit) {
          onEdit(question.id, { content: editedContent.trim() });
        }
        setIsEditing(false);
      } else {
        console.error('Failed to update question:', result.error);
        // Could show error toast here
      }
    } catch (error) {
      console.error('Error updating question:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedContent(question.content || '');
  };

  const handleDelete = async (questionId, userId, reason) => {
    try {
      console.log('QuestionDetail: About to delete question:', questionId);
      const result = await deleteQuestion(questionId, userId, reason);
      console.log('QuestionDetail: Delete result:', result);
      
      if (result.success && onDelete) {
        console.log('QuestionDetail: Calling onDelete callback');
        onDelete(questionId);
      } else if (!result.success) {
        console.error('Failed to delete question:', result.error);
        // You could show a toast notification here
      }
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  const handleEdit = (questionId) => {
    handleEditContent();
  };

  if (!question) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6" data-testid="loading-skeleton">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  // Filter out preset tags for display
  const displayTags = filterPresetTags(question.tags || []);

  return (
    <div className="forum-thread">
      <article className="question-content">
        {/* Question Title - Mobile Optimized */}
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 leading-tight">
          {question.title}
        </h1>
        
        {/* Author and Meta Information - Mobile Optimized */}
        <div className="flex items-start justify-between mb-4 sm:mb-6">
          <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
            <Link
              to={`/network/${question.authorId}`}
              target="_blank"
              rel="noopener"
              className="cursor-pointer hover:opacity-80 transition-opacity duration-200 flex-shrink-0"
            >
              {currentProfilePicture ? (
                <img
                  src={currentProfilePicture}
                  alt={question.authorName}
                  className="h-8 w-8 sm:h-10 sm:w-10 rounded-full object-cover"
                />
              ) : (
                <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                  <UserIcon className="h-4 w-4 sm:h-6 sm:w-6 text-purple-600 dark:text-purple-400" data-testid="user-icon" />
                </div>
              )}
            </Link>
            <div className="flex-1 min-w-0">
              <Link
                to={`/network/${question.authorId}`}
                target="_blank"
                rel="noopener"
                className="text-sm sm:text-base font-medium text-gray-900 dark:text-white hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-200 block truncate"
              >
                {question.authorName}
              </Link>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {formatDate(question.createdAt)}
                {question.updatedAt && question.updatedAt !== question.createdAt && (
                  <span className="hidden sm:inline"> • edited {formatDate(question.updatedAt)}</span>
                )}
              </p>
            </div>
          </div>

          {/* Moderation and Deletion Controls - Compact on mobile */}
          <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
            <ModerationControls
              item={question}
              type="question"
              onModerationAction={(questionId, action) => {
                // Handle moderation action - could update question state or redirect
                if (action === 'delete') {
                  // Optionally redirect or show deleted state
                }
              }}
            />

            <DeletionControls
              item={question}
              type="question"
              onDelete={handleDelete}
              onEdit={handleEdit}
            />
          </div>
        </div>

        {/* Tags - Mobile Optimized */}
        {displayTags.length > 0 && (
          <div className="mb-4 sm:mb-6 flex flex-wrap gap-1.5 sm:gap-2">
            {displayTags.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-0.5 sm:px-2.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Question Content - Mobile Optimized */}
        <div className="mb-4 sm:mb-6">
          {isEditing ? (
            <div className="relative" ref={editContainerRef}>
              <div className="mb-2 text-sm text-purple-600 dark:text-purple-300 flex items-center">
                <PencilIcon className="h-4 w-4 mr-1" /> 
                Edit your post below (click outside to save and exit):
              </div>
              <UserMentionInput
                value={editedContent}
                onChange={setEditedContent}
                placeholder="Edit your question content. Use @ to mention other users!"
                maxLength={5000}
                error={false}
                disabled={isSaving}
                className="min-h-[160px]"
              />
              <div className="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdits}
                    disabled={isSaving}
                    className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200 disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
                <span>Click outside to save automatically</span>
              </div>
            </div>
          ) : (
            <div
              ref={contentRef}
              className="prose prose-sm sm:prose-base max-w-none dark:prose-invert prose-purple plain-text-content"
            >
              <ContentWithMentions 
                content={question.content || ''} 
                className=""
              />
            </div>
          )}
          
          {/* Question Attachments */}
          {question.attachments && (
            <AttachmentDisplay 
              attachments={question.attachments}
              className="mt-4"
            />
          )}
        </div>

        {/* Thread Actions - Mobile Optimized */}
        <MobilePostActions
          viewCount={question.viewCount || 0}
          replyCount={question.replyCount || 0}
          likeCount={question.likeCount || 0}
          targetId={question.id}
          targetType="question"
          authorId={question.authorId}
          onLike={onLikeUpdate}
          onReply={user ? () => openModal({
            id: question.id,
            type: 'question',
            title: question.title,
            content: question.content,
            authorName: question.authorName,
            authorPhotoURL: question.authorPhotoURL,
            authorId: question.authorId,
            createdAt: question.createdAt
          }) : undefined}
          size="md"
        />
      </article>

      {/* Reply Modal */}
      <ReplyModal
        isOpen={isOpen}
        onClose={closeModal}
        title="Reply to Post"
      >
        {originalPost && (
          <>
            <OriginalPostPreview post={originalPost} />
            <ReplyComposer
              questionId={question.id}
              onSubmit={(reply) => {
                closeModal();
                if (onReplySubmitted) {
                  onReplySubmitted(reply);
                }
              }}
              onCancel={closeModal}
              autoFocus={true}
            />
          </>
        )}
      </ReplyModal>
    </div>
  );
};

export default QuestionDetail;